// tab/src/components/common/ErrorBoundary.tsx
// OnRounds Phase 2 - Error Boundary Component

import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ErrorCircle24Regular, ArrowClockwise24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '300px',
    ...shorthands.padding('20px'),
  },
  card: {
    maxWidth: '500px',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  icon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '32px',
  },
  content: {
    ...shorthands.padding('16px'),
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.gap('12px'),
    marginTop: '16px',
  },
  details: {
    marginTop: '16px',
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    fontSize: tokens.fontSizeBase200,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '150px',
    ...shorthands.overflow('auto'),
  },
});

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Functional wrapper to use hooks
const ErrorDisplay: React.FC<{
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
}> = ({ error, errorInfo, onReset }) => {
  const styles = useStyles();

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          image={<ErrorCircle24Regular className={styles.icon} />}
          header={<Text weight="semibold" size={500}>Something went wrong</Text>}
          description="An unexpected error occurred"
        />
        <div className={styles.content}>
          <Text>
            We're sorry, but something went wrong. Please try refreshing the page or contact support if the problem persists.
          </Text>

          <div className={styles.actions}>
            <Button
              appearance="primary"
              icon={<ArrowClockwise24Regular />}
              onClick={handleReload}
            >
              Refresh Page
            </Button>
            <Button appearance="secondary" onClick={onReset}>
              Try Again
            </Button>
          </div>

          {process.env.NODE_ENV === 'development' && error && (
            <div className={styles.details}>
              <strong>{error.name}:</strong> {error.message}
              {errorInfo?.componentStack && (
                <>
                  {'\n\n'}
                  <strong>Component Stack:</strong>
                  {errorInfo.componentStack}
                </>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // You could send error to logging service here
    // logErrorToService(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
